import React from 'react';
import { motion } from 'framer-motion';
import { CalendarPlus, GraduationCap, Users, VenetianMask } from 'lucide-react';
import { useCountUp } from '../../hooks/useCountUp';
import type { StudentStats } from '../../utils/stats';
interface StatCardProps {
  label: string;
  value: number;
  hint: string;
  icon: typeof Users;
  accent: string;
  index: number;
}
function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  accent,
  index
}: StatCardProps) {
  const animated = useCountUp(value);
  return <motion.article initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    delay: index * 0.06
  }} className="glass group relative overflow-hidden rounded-3xl p-5" data-id="element-63" data-mp="2rj5np07udwx9">
      <div className="flex items-start justify-between gap-3" data-id="element-64" data-mp="2hkprwjxvdzhf">
        <div className="min-w-0" data-id="element-65" data-mp="1b5ffpidlqoic">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-66" data-mp="1b6qgrafnxi5r">{label}</p>
          <p className="mt-2 font-mono text-3xl font-semibold tracking-tight text-foreground" data-id="element-67" data-mp="164re62rqyp9v">{animated}</p>
        </div>
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${accent}`} data-id="element-68" data-mp="gtlq3cs00lz">
          <Icon className="h-5 w-5" strokeWidth={1.9} data-id="element-69" data-mp="5felk1o48i9w" />
        </span>
      </div>
      <p className="mt-3 truncate text-xs text-muted-foreground" data-id="element-70" data-mp="3mpmg6fr8qy8h">{hint}</p>
      <span className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full brand-gradient-soft opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" data-id="element-71" data-mp="3rnxm4fef0olf" />
    </motion.article>;
}
export function StatsGridSkeleton() {
  return <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-72" data-mp="33zmr3svehj52">
      {Array.from({
      length: 4
    }).map((_, index) => <div key={index} className="glass rounded-3xl p-5" data-id="element-73" data-mp="38xoa6dsttigj" data-mpi={index}>
          <div className="flex items-start justify-between" data-id="element-74" data-mp="2uyoonf0m1jol">
            <div className="w-2/3 space-y-3" data-id="element-75" data-mp="2uz85stifs41k">
              <div className="shimmer h-3 w-3/4 rounded-full" data-id="element-76" data-mp="2z01w1tjcpuxm" />
              <div className="shimmer h-7 w-1/2 rounded-lg" data-id="element-77" data-mp="2yzia81qkymeb" />
            </div>
            <div className="shimmer h-11 w-11 rounded-2xl" data-id="element-78" data-mp="15icn73pijych" />
          </div>
          <div className="shimmer mt-4 h-2.5 w-2/3 rounded-full" data-id="element-79" data-mp="10qkt4j75fahl" />
        </div>)}
    </div>;
}
export function StatsGrid({
  stats
}: {
  stats: StudentStats;
}) {
  const genderHint = `${stats.male} male · ${stats.female} female · ${stats.other} other`;
  return <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-80" data-mp="1ogf3mu3zwmyo">
      <StatCard index={0} label="Total students" value={stats.total} hint="Live documents in the students collection" icon={Users} accent="bg-indigo-500/12 text-indigo-600 dark:text-indigo-300" data-id="element-81" data-mp="1ofnlakzeslqc" />
      <StatCard index={1} label="Total classes" value={stats.classes} hint={stats.classes === 0 ? 'No classes recorded yet' : 'Distinct classes across all records'} icon={GraduationCap} accent="bg-violet-500/12 text-violet-600 dark:text-violet-300" data-id="element-82" data-mp="1ah73nz00datf" />
      <StatCard index={2} label="Gender breakdown" value={stats.male + stats.female + stats.other} hint={stats.total === 0 ? 'Awaiting your first record' : genderHint} icon={VenetianMask} accent="bg-cyan-500/12 text-cyan-600 dark:text-cyan-300" data-id="element-83" data-mp="2apdtdpt60w8l" />
      <StatCard index={3} label="Recent additions" value={stats.recent} hint="Records added in the last 7 days" icon={CalendarPlus} accent="bg-emerald-500/12 text-emerald-600 dark:text-emerald-300" data-id="element-84" data-mp="2ogiocell90cl" />
    </div>;
}